/* ===================================================================
   Weekly updates bar — reveal, dismiss, remember
   -------------------------------------------------------------------
   Three jobs, no dependencies:

     1. Hold the bar back until the reader is past the threshold the
        section sets (data-scroll-depth, a percentage of scrollable
        page height). Nobody who bounced in the first screen ever sees
        it.
     2. Close it on the X, on Escape, and remember that for 30 days.
     3. Never show it again to someone who has already subscribed.

   Memory is localStorage keyed by intent, not by page, so dismissing
   it on one article silences it across the whole blog. A private
   window or blocked storage throws on read — every access is wrapped,
   and a throw degrades to "no memory", which shows the bar. That is
   the right failure: an unwanted bar the visitor can close again, not
   a bar that can never appear.

   The form itself is a native {% form 'customer' %}. It submits with a
   full page load like every other form in this theme, so there is no
   fetch path to get wrong and the bar still works with JS disabled —
   it simply renders visible from the start.
   =================================================================== */

(function () {
  'use strict';

  var DISMISS_KEY = 'veaudry:weekly-bar:dismissed';
  var SUBSCRIBED_KEY = 'veaudry:weekly-bar:subscribed';
  var DISMISS_DAYS = 30;
  var DAY_MS = 86400000;

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* Private mode, blocked site data, quota. Nothing to recover: the
         bar just forgets, which is the harmless direction. */
    }
  }

  /* A dismissal older than DISMISS_DAYS has expired, so the bar comes
     back. Subscribing silences it permanently. */
  function isSilenced() {
    if (read(SUBSCRIBED_KEY)) return true;

    var stamp = parseInt(read(DISMISS_KEY), 10);
    if (!stamp) return false;
    return Date.now() - stamp < DISMISS_DAYS * DAY_MS;
  }

  function init(bar) {
    var closeBtn = bar.querySelector('[data-vwb-close]');

    /* Set by the section when Shopify replays the form after a
       successful post. The bar shows itself immediately in that state,
       ignoring the scroll threshold — the confirmation is the whole
       reason the page reloaded. */
    var posted = bar.hasAttribute('data-posted');

    /* Same for a validation error: the visitor is mid-task, so the bar
       must be on screen to show them what went wrong. */
    var hasError = bar.hasAttribute('data-has-error');

    /* In the theme editor the bar must be visible the moment it is
       selected, or a merchant editing its copy sees an empty page and
       assumes the section is broken. No scroll gate, no storage. */
    var designMode = !!(window.Shopify && window.Shopify.designMode);

    if (designMode) {
      window.requestAnimationFrame(function () {
        bar.classList.add('vwb--in');
      });
      var editorClose = bar.querySelector('[data-vwb-close]');
      if (editorClose) {
        editorClose.addEventListener('click', function () {
          bar.classList.remove('vwb--in');
        });
      }
      return;
    }

    if (posted) {
      write(SUBSCRIBED_KEY, String(Date.now()));
    }

    if (!posted && !hasError && isSilenced()) {
      bar.parentNode.removeChild(bar);
      return;
    }

    /* Shopify's cookie-consent banner is fixed to the same bottom strip
       this bar docks into, and it paints above it. A first-time visitor
       would get the two stacked, with the banner swallowing every click
       aimed at the bar — including its close button. So the bar waits
       the banner out. Checked live rather than assumed: the banner is
       absent entirely once a visitor has answered it, and in markets
       where Shopify does not show one it never appears at all. */
    function consentBannerVisible() {
      var banner = document.querySelector('#shopify-pc__banner, .shopify-pc__banner');
      if (!banner) return false;

      /* Measured, not inferred. The obvious test — offsetParent === null
         — is wrong here: offsetParent is ALWAYS null on a position: fixed
         element, which is exactly what this banner is, so it would report
         every banner as hidden and the gate would never close. A zero
         box plus the two properties that can hide an element without
         collapsing it is what actually answers the question. */
      var style = window.getComputedStyle(banner);
      if (style.display === 'none' || style.visibility === 'hidden') return false;

      var box = banner.getBoundingClientRect();
      return box.height > 0 && box.width > 0;
    }

    var bannerPoll = null;

    function show() {
      if (consentBannerVisible()) {
        /* Poll rather than observe: the banner is removed by Shopify's
           own script from outside the theme, and it does not always
           mutate an element we can watch. One check a second costs
           nothing and stops the moment it pays off. */
        if (bannerPoll === null) {
          bannerPoll = window.setInterval(function () {
            if (!consentBannerVisible()) {
              window.clearInterval(bannerPoll);
              bannerPoll = null;
              bar.classList.add('vwb--in');
            }
          }, 1000);
        }
        return;
      }

      bar.classList.add('vwb--in');
    }

    function hide() {
      bar.classList.remove('vwb--in');
    }

    function dismiss() {
      write(DISMISS_KEY, String(Date.now()));
      hide();
      /* Wait out the slide before removing, so the bar animates away
         instead of vanishing. Matches the 0.42s transition in the CSS. */
      window.setTimeout(function () {
        if (bar.parentNode) bar.parentNode.removeChild(bar);
      }, 500);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', dismiss);
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && bar.classList.contains('vwb--in')) {
        dismiss();
      }
    });

    if (posted || hasError) {
      /* rAF so the browser paints the parked position first and the bar
         slides in rather than appearing already docked. */
      window.requestAnimationFrame(show);

      if (posted) {
        /* A confirmation has nothing left to do. Clear it rather than
           leave a permanent strip across the foot of the page. */
        window.setTimeout(hide, 6000);
      }
      return;
    }

    var depth = parseInt(bar.getAttribute('data-scroll-depth'), 10) || 55;
    var ticking = false;

    function measure() {
      ticking = false;

      var scrollable = document.documentElement.scrollHeight - window.innerHeight;

      /* A page shorter than the viewport can never reach the threshold.
         Show it at the foot of the scroll instead of never. */
      if (scrollable <= 0) {
        show();
        cleanup();
        return;
      }

      if (window.scrollY / scrollable >= depth / 100) {
        show();
        cleanup();
      }
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    function cleanup() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    /* Covers a reader who lands deep-linked at an anchor, or restores a
       scroll position on back-navigation. */
    measure();
  }

  function boot() {
    var bars = document.querySelectorAll('[data-weekly-bar]');
    for (var i = 0; i < bars.length; i++) {
      init(bars[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
